import type { AgentTemplateCardView } from "../api/gateway.js";
import { extendDisplayView, validatedDisplayView } from "./display-view.js";
import { portalLocale, portalResources, portalText, type PortalKey } from "./portal.js";

const keys = new Map<string, PortalKey<"agentTemplate">>(Object.entries(portalResources.en.agentTemplate).map(([key, value]) => [value, key as PortalKey<"agentTemplate">]));

/** Platform template metadata is display copy; greetings, schemas and build inputs remain canonical. */
export function agentTemplateText(value: string): string {
  if (portalLocale() === "en") return value;
  const key = keys.get(value);
  return key === undefined ? value : portalText("agentTemplate", key);
}

const outcomeVocabularyByLocale = new WeakMap<readonly string[], { readonly locale: string; readonly label: readonly string[] }>();

function outcomeVocabularyOf(outcome: readonly string[]): readonly string[] {
  const locale = portalLocale();
  const cached = outcomeVocabularyByLocale.get(outcome);
  if (cached !== undefined && cached.locale === locale) return cached.label;
  const label = outcome.map(agentTemplateText);
  outcomeVocabularyByLocale.set(outcome, { locale, label });
  return label;
}

export function localizeAgentTemplateCard(card: AgentTemplateCardView): AgentTemplateCardView {
  // Wizard effects depend on this array: preserve identity while labels follow the locale.
  const fieldSet = card.fieldSet.map((field) => {
    const copy = { get label() { return agentTemplateText(field.label); } };
    if (field.kind === "choice") return extendDisplayView(field, {
      get label() { return agentTemplateText(field.label); },
      options: field.options.map((option) => extendDisplayView(option, { get label() { return agentTemplateText(option.label); } })),
    });
    return extendDisplayView(field, copy);
  });
  return validatedDisplayView(extendDisplayView(card, {
    get name() { return agentTemplateText(card.name); },
    get dek() { return agentTemplateText(card.dek); },
    get youAdd() { return agentTemplateText(card.youAdd); },
    get contentTitle() { return agentTemplateText(card.contentTitle); },
    get contentDek() { return agentTemplateText(card.contentDek); },
    get successMetricLabel() { return agentTemplateText(card.successMetricLabel); },
    // plan-018 CAT (2026-10-13): the outcome labels follow the locale too ("Message taken" / "Καταγράφηκε μήνυμα"),
    // one array per locale so a reader's identity checks see a change only when the language changes.
    get outcomeVocabulary() { return outcomeVocabularyOf(card.outcomeVocabulary); },
    fieldSet,
  }));
}
